import {
  createBrowserRouter,
  type RouteObject,
  RouterProvider,
} from 'react-router-dom';
import { Toaster } from 'sonner';

import { CareersPage } from '@/pages/careers/CareersPage';
import { FreeShortlist } from '@/pages/shortlist/FreeShortlist';
import { ShortlistCandidateDetail } from '@/pages/shortlist/ShortlistCandidateDetail';

import { QueryClientProvider } from './lib/react-query/QueryClientProvider';
import { NotFound } from './modules/errors/components/NotFound';
import { RouteErrorBoundary } from './modules/errors/components/RouteErrorBoundary';
import { PortalLayoutSkeleton } from './modules/layouts/components/PortalLayoutSkeleton';
import { ProtectedLayout } from './modules/layouts/components/ProtectedLayout';
import { ProtectedRoute } from './modules/layouts/components/ProtectedRoute';
import { SignupFlowLayout } from './modules/layouts/components/SignupFlowLayout';
import { pages } from './modules/layouts/pages';
import { TermsGate } from './modules/legal/components/TermsGate';
import { CompanyIntake } from './pages/companies/CompanyIntake';
import { CandidateCoordinationMeeting } from './pages/coordination/CandidateCoordinationMeeting';
import { PermissionDenied } from './pages/errors/PermissionDenied';
import { HMApp } from './pages/hm/HMApp';
import { SlackCallback } from './pages/integrations/slackCallback/SlackCallback';
import { LinkedInCallback } from './pages/login/LinkedInCallback';
import Login from './pages/login/Login';
import { SlackSignInCallback } from './pages/login/SlackSignInCallback';
import { OAuthAuthorize } from './pages/oauth/OAuthAuthorize';
import { OAuthConsent } from './pages/oauth/OAuthConsent';
import {
  CandidatePrescreen,
  CandidatePrescreenInvite,
} from './pages/prescreen/CandidatePrescreen';
import { SignUp } from './pages/signUp/SignUp';

const routes: RouteObject[] = [
  {
    path: '/:company/shortlist/:roleName',
    element: <FreeShortlist />,
  },
  {
    path: '/:company/shortlist/:roleName/:candidateJobId',
    element: <ShortlistCandidateDetail />,
  },
  {
    path: '/:companySlug/careers',
    element: <CareersPage />,
  },
  {
    path: '/:companySlug/careers/:jobId',
    element: <CareersPage />,
  },
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/sign-up',
    element: <SignUp />,
  },
  {
    path: '/auth/callback/linkedin',
    element: <LinkedInCallback />,
  },
  {
    path: '/auth/callback/slack',
    element: <SlackSignInCallback />,
  },
  {
    path: '/sign-up/get-started',
    element: (
      <ProtectedRoute>
        <TermsGate>
          <SignupFlowLayout />
        </TermsGate>
      </ProtectedRoute>
    ),
  },
  {
    path: '/slack/callback/:status',
    element: <SlackCallback />,
  },
  {
    path: '/prescreen/:candidateJobId',
    element: <CandidatePrescreen />,
  },
  {
    path: '/i/:slug',
    element: <CandidatePrescreenInvite />,
  },
  {
    path: '/coordination/:meetingId',
    element: <CandidateCoordinationMeeting />,
  },
  {
    path: '/company/:jobId/intake',
    element: (
      <ProtectedRoute>
        <CompanyIntake />
      </ProtectedRoute>
    ),
  },
  {
    path: '/oauth/authorize',
    element: (
      <ProtectedRoute>
        <OAuthAuthorize />
      </ProtectedRoute>
    ),
  },
  {
    path: '/oauth/consent',
    element: (
      <ProtectedRoute>
        <OAuthConsent />
      </ProtectedRoute>
    ),
  },
  {
    path: '/permission-denied',
    element: (
      <ProtectedRoute>
        <PermissionDenied />
      </ProtectedRoute>
    ),
  },
  {
    path: '/hm/*',
    element: (
      <ProtectedRoute loadingFallback={<PortalLayoutSkeleton />}>
        <TermsGate>
          <HMApp />
        </TermsGate>
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: <ProtectedLayout />,
    children: [
      ...pages.map((page) => ({
        path: page.path === '/' ? '' : page.path.replace(/^\//, ''),
        element: page.component,
      })),
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

const router = createBrowserRouter([
  // One boundary for every route, including HMApp's nested <Routes>.
  { errorElement: <RouteErrorBoundary />, children: routes },
]);

const App = () => {
  return (
    <QueryClientProvider>
      <Toaster position="top-right" offset={65} richColors />
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
};

export default App;
